import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { App } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { AssetService } from '~/app/services/asset/asset.service';
import { KmsDocumentService } from '~/app/services/kms/kms-document.service';
import type { KmsHitlQueueItem } from '~/app/services/kms/kms-document.types';
import {
  enrichHitlCandidateLabels,
  isAssetLinkHitlReview,
  isOrgWideKmsScope,
  normalizeAssetLinkCandidates,
  normalizeHitlCandidates,
  parseAssetIdFromKmsScope,
  resolveKmsLinkerCandidates,
} from '~/app/services/kms/kms-document.utils';
import { KMS_DOCUMENT_TYPE_OPTIONS } from '../../KmsUploadPanel/utils';
import { useKmsHitlQueueColumns } from './useKmsHitlQueueColumns';

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

export function useKmsHitlQueue(params: {
  organizationId: string | null;
  isSuperAdmin: boolean;
  isPlatformLibrary: boolean;
}) {
  const { organizationId, isSuperAdmin, isPlatformLibrary } = params;
  const { t } = useTranslation('kms');
  const { notification } = App.useApp();
  const { hasPermission } = usePermission();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [selectedItem, setSelectedItem] = useState<KmsHitlQueueItem | null>(
    null,
  );
  const [selectedCandidateKey, setSelectedCandidateKey] = useState<
    string | undefined
  >();
  const [overrideAssetId, setOverrideAssetId] = useState<string | undefined>();
  const [resolvedDocumentType, setResolvedDocumentType] = useState<
    string | undefined
  >();

  const canResolve = hasPermission(PermissionKey.DOCUMENTS_HITL_RESOLVE);
  // Org Manager / Supervisor: org-wide global. Super Admin: platform or org-wide.
  const canMarkGlobal = canResolve;

  const queueEnabled = isPlatformLibrary || Boolean(organizationId);

  const queueQuery = useQuery({
    queryKey: [
      'kms',
      'hitl-queue',
      page,
      pageSize,
      organizationId,
      deferredSearch,
    ],
    queryFn: () =>
      KmsDocumentService.listHitlQueue({
        page,
        limit: pageSize,
        organizationId,
        isSuperAdmin,
        q: deferredSearch.trim() || undefined,
      }),
    enabled: queueEnabled,
    refetchInterval: 15_000,
  });

  const assetsQuery = useQuery({
    queryKey: ['kms', 'hitl-assets', organizationId],
    queryFn: () =>
      AssetService.listByOrganization({
        organizationId: organizationId!,
        page: 1,
        limit: 100,
      }),
    enabled:
      Boolean(organizationId) && !isPlatformLibrary && Boolean(selectedItem),
  });

  const typeCandidates = useMemo(
    () => normalizeHitlCandidates(selectedItem?.classificationCandidates),
    [selectedItem],
  );

  const assetLinkCandidates = useMemo(
    () =>
      normalizeAssetLinkCandidates(
        selectedItem ? resolveKmsLinkerCandidates(selectedItem) : undefined,
      ),
    [selectedItem],
  );

  const isAssetLinkReview = useMemo(
    () =>
      selectedItem
        ? isAssetLinkHitlReview({
            linkCandidates: selectedItem.linkCandidates,
            linkScope: selectedItem.linkScope,
          })
        : false,
    [selectedItem],
  );

  const assetOptions = useMemo(
    () =>
      (assetsQuery.data?.data ?? []).map((asset) => ({
        value: asset.id,
        label: asset.assetName || asset.id,
      })),
    [assetsQuery.data],
  );

  const assetNameById = useMemo(
    () => new Map(assetOptions.map((option) => [option.value, option.label])),
    [assetOptions],
  );

  const displayCandidates = useMemo(() => {
    const base = isAssetLinkReview ? assetLinkCandidates : typeCandidates;
    return enrichHitlCandidateLabels(base, assetNameById);
  }, [assetLinkCandidates, assetNameById, isAssetLinkReview, typeCandidates]);

  const documentTypeOptions = useMemo(
    () =>
      KMS_DOCUMENT_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    [t],
  );

  const resolveMutation = useMutation({
    mutationFn: (body: {
      asset_id: string | null;
      is_global: boolean;
      classified_document_type?: string | null;
    }) => {
      if (!selectedItem) {
        throw new Error('No document selected');
      }
      return KmsDocumentService.resolveHitl(
        selectedItem.documentId,
        body,
        organizationId,
        isSuperAdmin,
      );
    },
    onSuccess: async () => {
      notification.success({ message: t('hitl.resolveSuccess') });
      setSelectedItem(null);
      setSelectedCandidateKey(undefined);
      setOverrideAssetId(undefined);
      setResolvedDocumentType(undefined);
      await queryClient.invalidateQueries({ queryKey: ['kms', 'hitl-queue'] });
      await queryClient.invalidateQueries({
        queryKey: ['kms', 'linked-documents'],
      });
    },
    onError: (error) => {
      notification.error({
        message: t('hitl.resolveError'),
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  const onReview = useCallback((item: KmsHitlQueueItem) => {
    setSelectedItem(item);
    const assetReview = isAssetLinkHitlReview({
      linkCandidates: item.linkCandidates,
      linkScope: item.linkScope,
    });
    if (assetReview) {
      const top = normalizeAssetLinkCandidates(
        resolveKmsLinkerCandidates(item),
      )[0];
      setSelectedCandidateKey(top?.key);
      setOverrideAssetId(top?.assetId ?? undefined);
      setResolvedDocumentType(item.classifiedDocumentType ?? undefined);
      return;
    }
    const top = normalizeHitlCandidates(item.classificationCandidates)[0];
    setSelectedCandidateKey(top?.key);
    setOverrideAssetId(
      isOrgWideKmsScope(item.scope)
        ? undefined
        : (item.manualAssetId ??
            parseAssetIdFromKmsScope(item.scope) ??
            undefined),
    );
    setResolvedDocumentType(
      top?.documentType ?? item.classifiedDocumentType ?? undefined,
    );
  }, []);

  const isOrgWideReview = isOrgWideKmsScope(selectedItem?.scope);

  const onCandidateChange = useCallback(
    (key: string) => {
      setSelectedCandidateKey(key);
      const candidate = displayCandidates.find((entry) => entry.key === key);
      if (candidate?.documentType && !isAssetLinkReview) {
        setResolvedDocumentType(candidate.documentType);
      }
      if (candidate?.assetId) {
        setOverrideAssetId(candidate.assetId);
      }
    },
    [displayCandidates, isAssetLinkReview],
  );

  const columns = useKmsHitlQueueColumns({ onReview });

  const confirmSelected = useCallback(() => {
    const candidate = displayCandidates.find(
      (entry) => entry.key === selectedCandidateKey,
    );
    const finalType =
      resolvedDocumentType ??
      candidate?.documentType ??
      selectedItem?.classifiedDocumentType ??
      null;

    if (isAssetLinkReview) {
      const assetId = overrideAssetId ?? candidate?.assetId ?? null;
      if (!assetId) {
        notification.warning({ message: t('hitl.assetRequired') });
        return;
      }
      if (!finalType) {
        notification.warning({ message: t('hitl.typeRequired') });
        return;
      }
      void resolveMutation.mutateAsync({
        asset_id: assetId,
        is_global: false,
        classified_document_type: finalType,
      });
      return;
    }

    if (!finalType) {
      notification.warning({ message: t('hitl.typeRequired') });
      return;
    }

    // Type-first: Confirm always publishes organization-wide unless the
    // reviewer explicitly picked an asset in the optional override field.
    if (overrideAssetId) {
      void resolveMutation.mutateAsync({
        asset_id: overrideAssetId,
        is_global: false,
        classified_document_type: finalType,
      });
      return;
    }

    void resolveMutation.mutateAsync({
      asset_id: null,
      is_global: true,
      classified_document_type: finalType,
    });
  }, [
    displayCandidates,
    isAssetLinkReview,
    notification,
    overrideAssetId,
    resolveMutation,
    resolvedDocumentType,
    selectedCandidateKey,
    selectedItem?.classifiedDocumentType,
    t,
  ]);

  const overrideSelected = useCallback(() => {
    if (!overrideAssetId) {
      notification.warning({ message: t('hitl.overrideNeedsAsset') });
      return;
    }
    void resolveMutation.mutateAsync({
      asset_id: overrideAssetId,
      is_global: false,
      classified_document_type: resolvedDocumentType ?? null,
    });
  }, [notification, overrideAssetId, resolveMutation, resolvedDocumentType, t]);

  const markGlobal = useCallback(() => {
    void resolveMutation.mutateAsync({
      asset_id: null,
      is_global: true,
      classified_document_type: resolvedDocumentType ?? null,
    });
  }, [resolveMutation, resolvedDocumentType]);

  return {
    columns,
    data: queueQuery.data?.data ?? [],
    total: queueQuery.data?.total ?? 0,
    page,
    pageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    search,
    onSearchChange: (value: string) => {
      setSearch(value);
      setPage(1);
    },
    onPageChange: (nextPage: number, nextPageSize?: number) => {
      if (nextPageSize && nextPageSize !== pageSize) {
        setPageSize(nextPageSize);
        setPage(1);
        return;
      }
      setPage(nextPage);
    },
    isLoading: queueQuery.isLoading,
    selectedItem,
    closeModal: () => setSelectedItem(null),
    candidates: displayCandidates,
    isAssetLinkReview,
    selectedCandidateKey,
    onCandidateChange,
    overrideAssetId,
    setOverrideAssetId,
    resolvedDocumentType,
    setResolvedDocumentType,
    documentTypeOptions,
    assetOptions,
    assetsLoading: assetsQuery.isLoading,
    canResolve,
    canMarkGlobal,
    isPlatformLibrary,
    isOrgWideReview,
    confirmSelected,
    overrideSelected,
    markGlobal,
    isResolving: resolveMutation.isPending,
  };
}
